import { useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';

import { isPro, isUnlimited } from '@cartwhl/common/stores/userStore';
import { getStripePortalSession } from '@cartwhl/common/utils/getStripePortalSession';
import { getStripeProductID } from '@cartwhl/common/utils/getStripeProductID';
import {
  getStripeCustomerIdForBilling,
  isOrgCreditsWorkspace,
} from '@cartwhl/common/utils/organizationWorkspace';
import Text from '@cartwhl/ui/base/Text';
import Button from '@cartwhl/ui/input/Button';
import EmptyState from '@cartwhl/ui/messaging/EmptyState';

import { customerStore, productStore, userStore } from 'stores/global';

import CurrentPlan from './CurrentPlan';
import InvoiceAndUsage from './InvoiceAndUsage';
import ProfileSection from './ProfileSection';
import ProfileTabLoadingState from './ProfileTabLoadingState';

export default function PlanBilling() {
  const {
    loading: userLoading,
    userInfo,
    organizationInfo,
  } = useSnapshot(userStore.state);
  const orgCreditsWorkspace = isOrgCreditsWorkspace(organizationInfo);
  const {
    products,
    prices,
    loading: productsLoading,
  } = useSnapshot(productStore.state);
  const { customer } = useSnapshot(customerStore.state);

  const [requesting, setRequesting] = useState(false);
  const [isYearly, setIsYearly] = useState(false);

  useEffect(() => {
    productStore.fetchProducts();
  }, []);

  const stripeCustomerIdForBilling = getStripeCustomerIdForBilling(
    userInfo,
    organizationInfo,
  );

  useEffect(() => {
    if (stripeCustomerIdForBilling) {
      customerStore.fetchCustomer(stripeCustomerIdForBilling);
    }
  }, [stripeCustomerIdForBilling]);

  const paymentMethod = customer?.invoice_settings?.default_payment_method;
  const address = customer?.address;
  const paymentMethodLabel = getPaymentMethodLabel(paymentMethod);
  const stripeProProductID = getStripeProductID('pro');
  const monthlyProPriceAsYear = prices.find(
    (price) =>
      price.product === stripeProProductID &&
      price.recurring.interval === 'month',
  );
  const yearlyProPrice = prices.find(
    (price) =>
      price.product === stripeProProductID &&
      price.recurring.interval === 'year',
  );
  let savings = null;
  if (monthlyProPriceAsYear && yearlyProPrice) {
    savings = Math.floor(
      100 *
        (1 -
          yearlyProPrice.unit_amount /
            (12 * monthlyProPriceAsYear.unit_amount)),
    );
  }

  async function requestPortalSession() {
    if (!stripeCustomerIdForBilling) {
      return;
    }
    setRequesting(true);
    getStripePortalSession(stripeCustomerIdForBilling)
      .then(({ data }) => {
        if (typeof data !== 'undefined') {
          window.location.href = data.url;
        }
        setRequesting(false);
      })
      .catch(() => setRequesting(false));
  }

  const isBillingEnabled = isPro() || isUnlimited() || orgCreditsWorkspace;

  const showOrgBillingWithoutStripeId =
    orgCreditsWorkspace && !stripeCustomerIdForBilling;

  const canManageBillingPortal =
    isBillingEnabled && !showOrgBillingWithoutStripeId;

  const managePlanButton = (
    <Button
      variant="secondary"
      disabled={requesting || !stripeCustomerIdForBilling}
      loading={requesting}
      onClick={requestPortalSession}
    >
      Manage Plan
    </Button>
  );

  if (userLoading || productsLoading) {
    return <ProfileTabLoadingState />;
  }

  return (
    <div className="overview max-w-(--breakpoint-md) mx-auto">
      <ProfileSection title="Your plan" boxed={false} className="mt-8">
        <CurrentPlan />
      </ProfileSection>
      <ProfileSection
        title="Billing Details"
        className="mt-8"
        contentClassName="relative"
        actions={canManageBillingPortal ? managePlanButton : undefined}
      >
        {isBillingEnabled && showOrgBillingWithoutStripeId && (
          <EmptyState
            borderStyle="none"
            className="p-0"
            description="Stripe billing for your workspace is managed by an organization admin. Ask an admin to update payment methods or open the billing portal."
          />
        )}
        {canManageBillingPortal && (
          <>
            {paymentMethodLabel && (
              <div
                className={`flex w-full flex-row flex-nowrap items-end gap-4 pb-8 ${
                  address
                    ? 'border-gray-30 dark:border-gray-60 mb-8 border-b'
                    : ''
                }`}
              >
                <div>
                  <Text
                    as="label"
                    variant="studio/body/medium/sm"
                    className=" flex flex-col gap-2.5 "
                  >
                    Payment method
                  </Text>
                  {paymentMethodLabel.primaryLine && (
                    <Text
                      as="p"
                      variant="studio/body/medium/sm"
                      className="pb-1 pt-3 opacity-60 dark:opacity-70"
                    >
                      {paymentMethodLabel.primaryLine}
                    </Text>
                  )}
                  {paymentMethodLabel.secondaryLine && (
                    <Text
                      as="p"
                      variant="studio/body/medium/sm"
                      className="opacity-40 dark:opacity-50"
                    >
                      {paymentMethodLabel.secondaryLine}
                    </Text>
                  )}
                </div>
              </div>
            )}
            {address && (
              <div className="flex w-full flex-row flex-nowrap items-end gap-4">
                <div>
                  <Text
                    as="label"
                    variant="studio/body/medium/sm"
                    className=" flex flex-col gap-2.5 "
                  >
                    Billing address
                  </Text>
                  <Text
                    as="p"
                    variant="studio/body/medium/sm"
                    className="pb-1 pt-3 opacity-60 dark:opacity-70"
                  >
                    {address.line1}
                    <br />
                    {address.line2 && (
                      <>
                        {address.line2}
                        <br />
                      </>
                    )}
                    {address.city}, {address.state} {address.postal_code}
                    <br />
                    {address.country}
                  </Text>
                </div>
              </div>
            )}
            {!paymentMethodLabel && !address && (
              <Text
                as="p"
                variant="studio/body/medium/sm"
                className="opacity-60 dark:opacity-70"
              >
                Update payment methods, view invoices, or cancel your
                subscription in Stripe&apos;s billing portal.
              </Text>
            )}
          </>
        )}
        {!isBillingEnabled && (
          <EmptyState
            borderStyle="none"
            className="p-0"
            description="Upgrade your plan to Pro or Advanced to add billing details"
          />
        )}
      </ProfileSection>
      <ProfileSection
        title={orgCreditsWorkspace ? 'Credits & usage' : 'Invoices & Usage'}
        boxed={false}
        className="mt-8"
      >
        <InvoiceAndUsage />
      </ProfileSection>
    </div>
  );
}

type PaymentMethodLabel = {
  primaryLine: string;
  secondaryLine?: string;
};

type BillingPaymentMethod = {
  card?: {
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
  } | null;
  type?: string;
  link?: { email?: string | null };
};

function getPaymentMethodLabel(
  paymentMethod: BillingPaymentMethod | null | undefined,
): PaymentMethodLabel | null {
  if (!paymentMethod) {
    return null;
  }

  if (paymentMethod.type === 'link') {
    const linkEmail = paymentMethod.link?.email;
    return {
      primaryLine: linkEmail
        ? `Stripe Link (${linkEmail})`
        : 'Stripe Link',
    };
  }

  if (paymentMethod.card) {
    const { brand, last4, exp_month, exp_year } = paymentMethod.card;
    return {
      primaryLine: `${brand.charAt(0).toUpperCase()}${brand.slice(1)} ending in ${last4}`,
      secondaryLine: `Expires ${productStore.formatDigits(exp_month, 2)}/${productStore.formatDigits(exp_year, 4)}`,
    };
  }

  if (paymentMethod.type) {
    return {
      primaryLine: 'Payment method on file with Stripe',
    };
  }

  return null;
}
